import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Dog, User } from "../../types";
import { DogSelector } from "./DogSelector";
import { SelectWithFreeInput } from "../common/SelectWithFreeInput";
import { useAuth } from "../../contexts/AuthContext";
import { getDogActivityTrainerOptions } from "../../utils/trail/getDogActivityTrainerOptions";

// Data interface for getting/setting form data
export interface DetectionFormData {
  selectedDogId: string;
  duration: number;
  difficulty: "Easy" | "Moderate" | "Hard" | "Expert";
  targetScent: string;
  notes: string;
  trainer: string;
  trainerComment: string;
}

// Handle interface for parent component
export interface DetectionFormHandle {
  getData: () => DetectionFormData;
  setData: (data: Partial<DetectionFormData>) => void;
}

interface DetectionFormProps {
  user: User | null;
  dogsPlaceholder?: Dog[];
}

export const DetectionForm = forwardRef<
  DetectionFormHandle,
  DetectionFormProps
>(({ user, dogsPlaceholder }, ref) => {
  const isTrainer = user?.role.includes("trainer");
  const { dogs } = useAuth();

  // Internal state - all detection-specific fields
  const [selectedDogId, setSelectedDogId] = useState<string>("");
  const [duration, setDuration] = useState(0);
  const [difficulty, setDifficulty] = useState<
    "Easy" | "Moderate" | "Hard" | "Expert"
  >("Moderate");
  const [targetScent, setTargetScent] = useState("");
  const [notes, setNotes] = useState("");
  const [trainer, setTrainer] = useState("");
  const [trainerComment, setTrainerComment] = useState("");
  const [durationInMinutes, setDurationInMinutes] = useState<number | string>(
    "",
  );

  // Sync duration state (seconds <-> minutes)
  useEffect(() => {
    setDurationInMinutes(duration > 0 ? duration / 60 : "");
  }, [duration]);

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDurationInMinutes(value);
    if (value === "") {
      setDuration(0);
    } else {
      const minutes = Number(value);
      if (!isNaN(minutes) && minutes >= 0) {
        setDuration(minutes * 60);
      }
    }
  };

  // Filter trainers from the selected dog's populated trainers array
  const availableTrainersForDogOptions = useMemo(() => {
    if (!selectedDogId || !dogs || dogs.length === 0) {
      return [];
    }
    return getDogActivityTrainerOptions(
      dogs.find((dog) => dog._id === selectedDogId),
      'detection',
    );
  }, [selectedDogId, dogs]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getData: (): DetectionFormData => ({
      selectedDogId,
      duration,
      difficulty,
      targetScent,
      notes,
      trainer,
      trainerComment,
    }),
    setData: (data: Partial<DetectionFormData>) => {
      if (data.selectedDogId !== undefined)
        setSelectedDogId(data.selectedDogId);
      if (data.duration !== undefined) setDuration(data.duration);
      if (data.difficulty !== undefined) setDifficulty(data.difficulty);
      if (data.targetScent !== undefined) setTargetScent(data.targetScent);
      if (data.notes !== undefined) setNotes(data.notes);
      if (data.trainer !== undefined) setTrainer(data.trainer);
      if (data.trainerComment !== undefined)
        setTrainerComment(data.trainerComment);
    },
  }));

  return (
    <>
      <DogSelector
        value={selectedDogId}
        onChange={setSelectedDogId}
        required
        dogPlaceHolder={dogsPlaceholder}
      />

      <div className="space-y-2">
        <Label htmlFor="duration">Durée (minutes) *</Label>
        <Input
          id="duration"
          type="number"
          value={durationInMinutes}
          onChange={handleDurationChange}
          required
          placeholder="Ex: 30"
          min="0"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="difficulty">Difficulté</Label>
        <Select
          value={difficulty}
          onValueChange={(v) =>
            setDifficulty(v as "Easy" | "Moderate" | "Hard" | "Expert")
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner une difficulté" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Easy">Facile</SelectItem>
            <SelectItem value="Moderate">Modérée</SelectItem>
            <SelectItem value="Hard">Difficile</SelectItem>
            <SelectItem value="Expert">Expert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="targetScent">Odeur cible</Label>
        <Input
          id="targetScent"
          value={targetScent}
          onChange={(e) => setTargetScent(e.target.value)}
          placeholder="Ex: Cèdre, Lavande..."
        />
      </div>

      <SelectWithFreeInput
        id="trainer"
        label="Formateur"
        options={availableTrainersForDogOptions}
        value={trainer}
        onChange={setTrainer}
        placeholder="Sélectionnez un formateur"
        freeInputPlaceholder="Saisissez le nom du formateur"
      />

      {isTrainer && (
        <div className="space-y-2">
          <Label htmlFor="trainerComment">Commentaire du formateur</Label>
          <Textarea
            id="trainerComment"
            value={trainerComment}
            onChange={(e) => setTrainerComment(e.target.value)}
            placeholder="Ajoutez un commentaire visible uniquement par les formateurs..."
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="notes">Notes / Commentaire</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Commentaires sur la session de détection..."
        />
      </div>
    </>
  );
});

DetectionForm.displayName = "DetectionForm";
